import FingerprintJS from '@fingerprintjs/fingerprintjs'
import { browser, Cookies, CustomError, URL } from '@kulee/helper'
import { RESPONSE_CODES, STORAGE_KEYS } from '@kulee/tga-constant'
import BaseModule from '../../BaseModule'
import logger from '../../config/logger'
import type {
  LoginByAccountOptions,
  LoginOptions,
  RegisterByAccountBindEmailOptions,
  Token,
  UpdateUserInfoOptions,
  UserInfo
} from '../../types/uc'
import UCService from './Service'

class UC extends BaseModule {
  public static override clazz = 'UC'

  public service!: UCService

  private userTokenStorageKey!: string
  private userInfoStorageKey!: string

  public override run(): void {
    logger.log(`run module ${UC.clazz}`)

    this.service = new UCService(this.context)
    this.userTokenStorageKey = STORAGE_KEYS.USER_TOKEN + '-' + this.context.appConfig.appId
    this.userInfoStorageKey = STORAGE_KEYS.USER_INFO + '-' + this.context.appConfig.appId
  }

  /**
   * 应用初始化快速登录
   * @returns token
   */
  public async quickLogin(): Promise<Token> {
    logger.log('start to quick login, check kuleWebview: ', browser.version.kuleWebview)

    const { enabled, appLoginEnabled } = this.context.appConfig.feature.uc

    // 禁用用户中心
    if (!enabled) {
      this.removeTokenAndUser()
      throw new CustomError({
        code: RESPONSE_CODES.UC_DISABLED
      })
    }

    // 应用提供登录
    if (appLoginEnabled) {
      const loginParams: Pick<LoginOptions, 'fpId' | 'nickname' | 'avatar' | 'inviteCode'> = {}

      const query = URL.getUrlQuery()
      if (browser.version.kuleWebview) {
        const { txnId, nickName, avatar } = await window.Bridge.system.gameCenterUserParam()
        loginParams.fpId = txnId
        loginParams.nickname = nickName
        loginParams.avatar = avatar
      } else {
        loginParams.fpId = query['msisdn'] || query['txn-id']
        loginParams.nickname = query['nickname']
        loginParams.avatar = query['avatar']
      }
      loginParams.inviteCode = query['inviteCode']

      // 匿名登录
      if (loginParams.fpId) {
        const token = await this.loginWithFpId(loginParams)
        this.removeTokenAndUser()
        this.saveToken(token)
      }
    }

    // 3、从本地获取
    const token = this.getToken()
    if (token) {
      return token
    }

    throw new CustomError({
      code: RESPONSE_CODES.UC_LOGIN_FAILED
    })
  }

  /**
   * 检测是否登录
   * @returns 用户是否登录
   */
  public checkLogin(): boolean {
    const token = this.getToken()
    const user = this.getUser()

    const isLogin = !!token && !!user
    logger.log('check user login result: ', isLogin)
    return isLogin
  }

  /**
   * 帐号注册(同时绑定邮箱)
   * @param options 注册参数
   * @param options.email 邮箱
   * @param options.password 密码
   * @param options.code 验证码
   * @param options.inviteCode 邀请码
   * @returns token
   */
  public async registerByAccountBindEmail(options: RegisterByAccountBindEmailOptions): Promise<Token> {
    const { email, ...rest } = options
    const { data } = await this.service.register({
      account: email,
      email,
      ...rest
    })

    this.removeTokenAndUser()
    this.saveToken(data)

    return data
  }

  /**
   * 帐号登录
   * @param options 登录参数
   * @param options.account 帐号
   * @param options.password 密码
   * @returns token
   */
  public async loginByAccount(options: LoginByAccountOptions): Promise<Token> {
    const { account, password } = options
    const { data } = await this.service.login({
      account,
      password,
      type: 1
    })

    this.removeTokenAndUser()
    this.saveToken(data)

    return data
  }

  /**
   * 退出登录
   * @returns null
   */
  public async logout(): Promise<void> {
    if (this.getToken()) {
      this.removeTokenAndUser()
      await this.service.logout()
    }
    logger.log('user logout success')
  }

  /**
   * 从SDK登录
   * @returns token
   */
  public async loginFromSDK(): Promise<Token> {
    try {
      const { txnId } = await window.Bridge.uc.login()
      if (txnId) {
        const { token: accessToken, refreshToken } = await window.Bridge.system.gameCenterUserParam()

        this.removeTokenAndUser()
        this.saveToken({
          accessToken,
          refreshToken
        })
      }
    } catch (error) {
      logger.error('none sdk')
    }

    // 3、从本地获取
    const token = this.getToken()
    if (token) {
      return token
    }

    throw new CustomError({
      code: RESPONSE_CODES.UC_LOGIN_FAILED,
      message: 'Quick login failed.'
    })
  }

  /**
   * 更新用户信息
   * @param options 用户信息
   * @param options.nickname 昵称
   * @param options.avatar 头像
   * @param options.cover 封面
   * @returns null
   */
  public async updateUserInfo(options: UpdateUserInfoOptions): Promise<null> {
    await this.service.updateUserInfo(options)
    this.removeUser()

    return null
  }

  /**
   * 获取用户信息
   * @returns 用户信息
   */
  public async getUserInfo(): Promise<UserInfo> {
    const userInfo = this.getUser()
    if (userInfo) {
      return userInfo
    }

    return this.getUserInfoFromServer()
  }

  /**
   * 从服务端获取用户信息
   * @returns 用户信息
   */
  public async getUserInfoFromServer(): Promise<UserInfo> {
    const { data } = await this.service.getUserInfo()

    // 填充用户默认头像
    !data.avatar && (data.avatar = this.context.appConfig.feature.uc.defaultAvatar || '')
    // 填充用户默认封面
    !data.cover && (data.cover = this.context.appConfig.feature.uc.defaultCover || '')

    this.saveUser(data)

    logger.log('get user from server success')
    return data
  }

  /**
   * 获取用户token
   * @returns token或者null
   */
  private getToken(): Token | null {
    try {
      return JSON.parse(Cookies.get(this.userTokenStorageKey) || '')
    } catch (error) {
      return null
    }
  }

  /**
   * 存储用户token
   * @param token 用户登录的token信息
   */
  private saveToken(token: Token): void {
    const tokenStr = JSON.stringify(token)
    Cookies.set(this.userTokenStorageKey, tokenStr)
  }

  /**
   * 移除token
   */
  private removeToken(): void {
    Cookies.remove(this.userTokenStorageKey)
  }

  /**
   * 从本地获取用户信息
   * @returns 用户信息或者null
   */
  private getUser(): UserInfo | null {
    try {
      const userInfo = JSON.parse(Cookies.get(this.userInfoStorageKey) || '')

      return userInfo
    } catch (error) {
      return null
    }
  }

  /**
   * 存储用户信息
   * @param userInfo 用户信息
   */
  private saveUser(userInfo: UserInfo): void {
    const userInfoStr = JSON.stringify(userInfo)
    Cookies.set(this.userInfoStorageKey, userInfoStr)
  }

  /**
   * 移除用户信息
   */
  private removeUser(): void {
    Cookies.remove(this.userInfoStorageKey)
  }

  /**
   * 清除token和用户信息
   */
  private removeTokenAndUser(): void {
    this.removeToken()
    this.removeUser()
  }

  /**
   * fpId登录
   * @param options 登录参数
   * @param options.fpId fpId
   * @param options.nickname 用户昵称
   * @param options.avatar 用户头像
   * @returns token
   */
  private async loginWithFpId(
    options: Pick<LoginOptions, 'fpId' | 'nickname' | 'avatar' | 'inviteCode'> = {}
  ): Promise<Token> {
    const { fpId, nickname, avatar, inviteCode } = options

    let visitorFpId = fpId
    if (!fpId) {
      const fp = await FingerprintJS.load()
      visitorFpId = (await fp.get()).visitorId
    }

    const { data } = await this.service.login({
      fpId: visitorFpId as string,
      nickname,
      avatar,
      inviteCode,
      type: 4
    })

    return data
  }
}

export default UC
